import React, { useState } from 'react';
import styled from 'styled-components';
import { Pencil, Check, X, RotateCcw } from 'lucide-react';
import { BillingBreakdownDto, billingApi, formatCurrency, formatDate } from '../../services/paymentsService';

interface ChargeBreakdownProps {
  breakdown?: BillingBreakdownDto;
  billingId?: number;
  canEdit?: boolean;
  onMonthsUpdated?: () => void;
}

const Container = styled.div`
  padding: 20px 0;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const GridItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color-light);

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span<{ $secondary?: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$secondary ? 'var(--text-secondary)' : 'var(--text-primary)'};
`;

const Value = styled.span<{ $highlight?: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$highlight ? '#16a34a' : 'var(--text-primary)'};
  direction: ltr;
`;

const Divider = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  background: var(--border-color);
  margin: 8px 0;
`;

const TotalSection = styled.div`
  margin-top: 16px;
`;

const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
`;

const SubtotalLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const SubtotalValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  direction: ltr;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
  margin-top: 8px;
`;

const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const TotalValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
  direction: ltr;
`;

const NotesSection = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  color: var(--text-secondary);
`;

const NotesLabel = styled.span`
  font-weight: 600;
  margin-left: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: var(--text-secondary);
  font-size: 13px;
`;

const MonthsValueWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  border-radius: 4px;
  &:hover:not(:disabled) { color: var(--primary-color); background: var(--background-secondary); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const ManualBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  background: #fef3c7;
  border-radius: 4px;
  padding: 1px 6px;
`;

const MonthsInput = styled.input`
  width: 64px;
  padding: 4px 6px;
  font-size: 13px;
  text-align: center;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  direction: ltr;
  &:focus { outline: none; border-color: var(--primary-color); }
`;

const EditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InlineError = styled.span`
  font-size: 11px;
  color: #dc2626;
  margin-inline-start: 4px;
`;

const ChargeBreakdown: React.FC<ChargeBreakdownProps> = ({ breakdown, billingId, canEdit, onMonthsUpdated }) => {
  const [editingMonths, setEditingMonths] = useState(false);
  const [monthsInput, setMonthsInput] = useState('');
  const [monthsError, setMonthsError] = useState<string | null>(null);
  const [savingMonths, setSavingMonths] = useState(false);

  const canEditMonths = Boolean(canEdit && billingId);
  const isManual = breakdown?.manualNumberOfMonths != null;

  const validateMonths = (val: string): string | null => {
    if (!val.trim()) return 'שדה חובה';
    const n = Number(val);
    if (!Number.isInteger(n)) return 'מספר שלם בלבד';
    if (n < 1 || n > 60) return 'הזן מספר בין 1 ל-60';
    return null;
  };

  const startEditMonths = () => {
    setMonthsInput(breakdown?.numberOfMonths != null ? String(breakdown.numberOfMonths) : '');
    setMonthsError(null);
    setEditingMonths(true);
  };

  const saveMonths = async () => {
    const err = validateMonths(monthsInput);
    if (err) { setMonthsError(err); return; }
    if (!billingId) return;
    try {
      setSavingMonths(true);
      await billingApi.updateMonths(billingId, Number(monthsInput));
      setEditingMonths(false);
      onMonthsUpdated?.();
    } catch (e: any) {
      setMonthsError(e?.response?.data?.message || 'שגיאה בשמירה');
    } finally {
      setSavingMonths(false);
    }
  };

  const resetMonthsToAuto = async () => {
    if (!billingId) return;
    try {
      setSavingMonths(true);
      await billingApi.updateMonths(billingId, null);
      setEditingMonths(false);
      onMonthsUpdated?.();
    } catch (e: any) {
      setMonthsError(e?.response?.data?.message || 'שגיאה באיפוס');
    } finally {
      setSavingMonths(false);
    }
  };

  if (!breakdown) {
    return (
      <Container>
        <Title>פירוט החיוב</Title>
        <EmptyState>טרם הוזן מידע על חיובים</EmptyState>
      </Container>
    );
  }

  const hasMonthlyBenefits = (breakdown.monthlyBenefitAmount ?? 0) > 0;
  const hasRetroactive = (breakdown.retroactiveTotalAmount ?? 0) > 0;

  return (
    <Container>
      <Title>פירוט החיוב</Title>

      <Grid>
        {/* Committee and Commission */}
        {breakdown.committeeId && (
          <GridItem>
            <Label>מספר ועדה</Label>
            <Value>{breakdown.committeeId}</Value>
          </GridItem>
        )}
        <GridItem>
          <Label>אחוז עמלה</Label>
          <Value $highlight>{breakdown.commissionPercentage}%</Value>
        </GridItem>

        {/* Monthly Benefits Section */}
        {hasMonthlyBenefits && (
          <>
            <Divider />
            <GridItem>
              <Label $secondary>גמלה חודשית</Label>
              <Value>{formatCurrency(breakdown.monthlyBenefitAmount)}</Value>
            </GridItem>
            <GridItem>
              <Label $secondary>מספר חודשים (עתיד)</Label>
              {editingMonths ? (
                <EditRow>
                  <MonthsInput
                    type="number"
                    min={1}
                    max={60}
                    value={monthsInput}
                    autoFocus
                    disabled={savingMonths}
                    onChange={(e) => { setMonthsInput(e.target.value); if (monthsError) setMonthsError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveMonths(); if (e.key === 'Escape') { setEditingMonths(false); setMonthsError(null); } }}
                  />
                  <IconButton type="button" title="שמור" disabled={savingMonths} onClick={saveMonths}><Check size={16} /></IconButton>
                  <IconButton type="button" title="ביטול" disabled={savingMonths} onClick={() => { setEditingMonths(false); setMonthsError(null); }}><X size={16} /></IconButton>
                  {isManual && (
                    <IconButton type="button" title="אפס לחישוב אוטומטי" disabled={savingMonths} onClick={resetMonthsToAuto}><RotateCcw size={16} /></IconButton>
                  )}
                  {monthsError && <InlineError>{monthsError}</InlineError>}
                </EditRow>
              ) : (
                <MonthsValueWrap>
                  <Value>{breakdown.numberOfMonths ?? 0}</Value>
                  {isManual && <ManualBadge>ידני</ManualBadge>}
                  {canEditMonths && (
                    <IconButton type="button" title="עריכת מספר החודשים" onClick={startEditMonths}><Pencil size={14} /></IconButton>
                  )}
                </MonthsValueWrap>
              )}
            </GridItem>
            <GridItem>
              <Label $secondary>סה"כ גמלאות</Label>
              <Value>{formatCurrency(breakdown.totalBenefits)}</Value>
            </GridItem>
            <GridItem>
              <Label>עמלה על גמלאות</Label>
              <Value $highlight>{formatCurrency(breakdown.commissionOnBenefits)}</Value>
            </GridItem>
          </>
        )}

        {/* Retroactive Section */}
        {hasRetroactive && (
          <>
            <Divider />
            {breakdown.retroactiveFromDate && (
              <GridItem>
                <Label $secondary>רטרואקטיב מתאריך</Label>
                <Value>{formatDate(breakdown.retroactiveFromDate)}</Value>
              </GridItem>
            )}
            {(breakdown.retroactiveMonths ?? 0) > 0 && (
              <GridItem>
                <Label $secondary>מספר חודשי רטרו</Label>
                <Value>{breakdown.retroactiveMonths}</Value>
              </GridItem>
            )}
            <GridItem>
              <Label $secondary>סכום רטרואקטיב</Label>
              <Value>{formatCurrency(breakdown.retroactiveTotalAmount)}</Value>
            </GridItem>
            <GridItem>
              <Label>עמלה על רטרואקטיב</Label>
              <Value $highlight>{formatCurrency(breakdown.commissionOnRetroactive)}</Value>
            </GridItem>
          </>
        )}
      </Grid>

      {/* Total with VAT */}
      <TotalSection>
        <SubtotalRow>
          <SubtotalLabel>סה"כ עמלה</SubtotalLabel>
          <SubtotalValue>{formatCurrency(breakdown.totalCharge)}</SubtotalValue>
        </SubtotalRow>
        <SubtotalRow>
          <SubtotalLabel>מע"מ ({breakdown.vatRate}%)</SubtotalLabel>
          <SubtotalValue>{formatCurrency(breakdown.vatAmount)}</SubtotalValue>
        </SubtotalRow>
        <TotalRow>
          <TotalLabel>סה"כ לתשלום (כולל מע"מ)</TotalLabel>
          <TotalValue>{formatCurrency(breakdown.totalWithVat)}</TotalValue>
        </TotalRow>
      </TotalSection>

      {/* Notes */}
      {breakdown.notes && (
        <NotesSection>
          <NotesLabel>הערות:</NotesLabel>
          {breakdown.notes}
        </NotesSection>
      )}
    </Container>
  );
};

export default ChargeBreakdown;
